import { useEffect, useLayoutEffect, useRef } from 'react';

interface ScrollPosition {
  scrollTop: number;
  scrollLeft: number;
}

function loadPosition(key: string): ScrollPosition | null {
  try {
    const saved = sessionStorage.getItem(key);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as { scrollTop?: number; scrollLeft?: number };
    const top = typeof parsed.scrollTop === 'number' ? parsed.scrollTop : 0;
    const left = typeof parsed.scrollLeft === 'number' ? parsed.scrollLeft : 0;
    return { scrollTop: top, scrollLeft: left };
  } catch {
    return null;
  }
}

function saveToStorage(key: string, pos: ScrollPosition) {
  try {
    sessionStorage.setItem(key, JSON.stringify(pos));
  } catch {
    /* ignore */
  }
}

export interface UseScrollRestorationOptions {
  storageKey: string;
  /** Восстанавливать только когда контент готов (напр. !isLoading) */
  contentReady?: boolean;
  /** При изменении year/month — сбросить скролл в 0 (для расписания) */
  resetOnMonthChange?: { year: number; month: number };
}

export function useScrollRestoration(
  scrollRef: React.RefObject<HTMLElement | null>,
  isReady: boolean,
  options: UseScrollRestorationOptions
) {
  const {
    storageKey,
    contentReady = true,
    resetOnMonthChange,
  } = options;

  const prevMonthRef = useRef<{ year: number; month: number } | null>(null);
  const hasRestoredRef = useRef(false);
  const lastPositionRef = useRef<ScrollPosition | null>(null);

  const shouldRestore = isReady && contentReady;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !shouldRestore) return;

    const isMonthChange =
      resetOnMonthChange &&
      prevMonthRef.current !== null &&
      (prevMonthRef.current.year !== resetOnMonthChange.year ||
        prevMonthRef.current.month !== resetOnMonthChange.month);

    if (resetOnMonthChange) prevMonthRef.current = resetOnMonthChange;

    if (resetOnMonthChange && isMonthChange) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
      return;
    }

    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const pos = loadPosition(storageKey);
    if (pos && (pos.scrollTop > 0 || pos.scrollLeft > 0)) {
      lastPositionRef.current = pos;
      el.scrollTop = pos.scrollTop;
      el.scrollLeft = pos.scrollLeft;
    }
  }, [shouldRestore, storageKey, scrollRef, resetOnMonthChange?.year, resetOnMonthChange?.month]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isReady) return;

    let rafId: number;
    const handleScroll = () => {
      lastPositionRef.current = { scrollTop: el.scrollTop, scrollLeft: el.scrollLeft };
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (lastPositionRef.current) {
          saveToStorage(storageKey, lastPositionRef.current);
        }
      });
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (lastPositionRef.current) {
        saveToStorage(storageKey, lastPositionRef.current);
      }
    };
  }, [isReady, storageKey, scrollRef]);
}
